import { useAuth } from '@clerk/react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AuthCheck } from '@/components/auth-check.component';
import { Head } from '@/components/head.component';
import { OnboardingCheck } from '@/components/onboarding-check.component';
import { SequencingAccessGate } from '@/containers/sequences/sequencing-page.component';
import { SettingsLayout } from '@/containers/settings/settings-layout.component';
import { SequencerSettings } from '@/containers/sequences/sequencer-settings/sequencer-settings.component';
import {
  LEFT_SECTIONS,
  leftSequencerSection,
  SEQUENCER_SETTINGS_LABELS,
  sequencerSection,
  sequencerSectionAnchor,
  type LeftSequencerSection,
  type SequencerSettingsSection,
} from '@/containers/sequences/sequencer-settings/sequencer-settings-sections';

interface SettingsSearch {
  section?: SequencerSettingsSection | LeftSequencerSection;
  // Where on the page a retired section's link lands.
  at?: string;
  // The email connect flow: `connect=email` opens the provider picker, and
  // the hosted flow returns with `connected=1` or `connect_failed=1`.
  connect?: 'email';
  connected?: '1';
  connect_failed?: '1';
}

export const Route = createFileRoute('/sequencing/settings')({
  // Every key stays in the result, set or not: the router merges the raw
  // search under it, and a missing key would let a value that is not a
  // section through to a page that draws nothing for it.
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    // A section that moved opens the page that took its settings, so the
    // links and alerts that still carry it land where its settings are. One
    // that left for another page keeps its name, for the guard below.
    section:
      sequencerSection(search.section) ?? leftSequencerSection(search.section),
    at: sequencerSectionAnchor(search.section, search.at),
    connect: search.connect === 'email' ? 'email' : undefined,
    connected: search.connected !== undefined ? '1' : undefined,
    connect_failed: search.connect_failed !== undefined ? '1' : undefined,
  }),
  // `section=clu` opens Skills > Writing, where Clu writing is now.
  beforeLoad: ({ search }) => {
    const left = leftSequencerSection(search.section);
    if (left) throw redirect(LEFT_SECTIONS[left]);
  },
  component: SequencerSettingsRoute,
});

function SequencerSettingsRoute() {
  const search = Route.useSearch();
  const section = sequencerSection(search.section) ?? 'senders';
  const { orgId, userId } = useAuth();
  // A pane of Settings, whose nav lists the sections.
  return (
    <>
      <Head heading={SEQUENCER_SETTINGS_LABELS[section]} />
      <AuthCheck>
        <OnboardingCheck>
          <SettingsLayout>
            <SequencingAccessGate>
              <SequencerSettings
                key={`${orgId}:${userId}`}
                section={section}
                anchor={search.at}
              />
            </SequencingAccessGate>
          </SettingsLayout>
        </OnboardingCheck>
      </AuthCheck>
    </>
  );
}
